/* -----------------------------------------------------------------------------

THIS FILE DOES NOT USE AUTOMATIC DOCUMENTATION

YOU WILL NEED TO MANUALLY UPDATE Card.mdx WHEN CHANGING PROPS

Why, you ask? Well, Storybook 8 isn't parsing things right. So we have to do it
this way right now. Cool.

----------------------------------------------------------------------------- */

import { mergeClassNames } from '../../../utils/mergeClassNames';
import clsx from 'clsx';
import { HTMLAttributes, ReactNode } from 'react';
import {
  Button,
  ButtonProps,
  Link,
  LinkProps,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  ToggleButtonProps,
} from 'react-aria-components';
import styles from './Card.module.scss';

type CardRootBaseProps = {
  variant?: 'elevated' | 'outlined' | 'filled';
  padding?: 'medium' | 'small' | 'none';
  radius?: 'default' | 'inset' | 'inset-medium';
};

export type BetaCardRootContainerProps = CardRootBaseProps &
  HTMLAttributes<HTMLElement>;

export type BetaCardRootLinkProps = CardRootBaseProps & LinkProps;
export type BetaCardRootButtonProps = CardRootBaseProps & ButtonProps;
export type BetaCardRootToggleButtonProps = CardRootBaseProps &
  ToggleButtonProps;

/**
 * A card that functions as a non-interactive container. You may put interactive
 * content inside this root.
 */
export const BetaCardRootContainer = ({
  className,
  variant = 'elevated',
  padding = 'medium',
  radius = 'default',
  ...props
}: BetaCardRootContainerProps): ReactNode => {
  return (
    <div
      className={clsx(styles.root, className)}
      data-variant={variant}
      data-padding={padding}
      data-radius={radius}
      {...props}
    />
  );
};

/**
 * A card that functions as a link. Use `href` and `DesignSystemProvider` to
 * ensure client side routing.
 *
 * Does not render a disclosure indicator (chevron/arrow) automatically.
 * To show one, add an `<Icon>` explicitly (e.g. `arrowSmallRight`).
 *
 * ---
 *
 * @see https://react-spectrum.adobe.com/react-spectrum/Link.html#link
 * @see DesignSystemProvider
 */
export const BetaCardRootLink = ({
  className,
  variant = 'elevated',
  padding = 'medium',
  radius = 'default',
  ...props
}: BetaCardRootLinkProps): ReactNode => {
  return (
    <Link
      className={(renderProps) => {
        return mergeClassNames(
          [styles.root, styles.interactive, className],
          renderProps
        );
      }}
      data-variant={variant}
      data-padding={padding}
      data-radius={radius}
      {...props}
    />
  );
};

/**
 * A card that functions as a button.
 *
 * Does not render a disclosure indicator (chevron/arrow) automatically.
 * To show one, add an `<Icon>` explicitly (e.g. `arrowSmallRight`).
 *
 * ---
 *
 * **NOTE:** _You may **NOT** put interactive children inside this component._
 *
 * ---
 *
 * @see https://react-spectrum.adobe.com/react-spectrum/Button.html#button
 */
export const BetaCardRootButton = ({
  className,
  variant = 'elevated',
  padding = 'medium',
  radius = 'default',
  ...props
}: BetaCardRootButtonProps): ReactNode => {
  return (
    <Button
      className={(renderProps) => {
        return mergeClassNames(
          [styles.root, styles.interactive, className],
          renderProps
        );
      }}
      data-variant={variant}
      data-padding={padding}
      data-radius={radius}
      {...props}
    />
  );
};

/**
 * A card that can be used as a toggle button when alone. As a group, it
 * implements the ARIA radiogroup (single selection) or ARIA toolbar (multiple
 * selection).
 *
 * When wrapped in a BetaCardGroupToggleButton, use the arrow keys to navigate between
 * buttons within the group.
 *
 * ---
 *
 * **NOTE:** _Wrap in BetaCardGroupToggleButton when using multiple toggle button cards
 * together. The prop `id` is required to keep track of selected state._
 *
 * Use React's `useId()` to generate a safe ID prefix for your component if it
 * may render more than once one a page.
 *
 * ---
 *
 * **NOTE:** _You may **NOT** put interactive children inside this component._
 *
 * ---
 *
 * @see
 * https://react-spectrum.adobe.com/react-aria/CardGroupToggleButton.html#togglebuttongroup
 */
export const BetaCardRootToggleButton = ({
  className,
  variant = 'elevated',
  padding = 'medium',
  radius = 'default',
  ...props
}: BetaCardRootToggleButtonProps): ReactNode => {
  return (
    <ToggleButton
      className={(renderProps) => {
        return mergeClassNames(
          [styles.root, styles.interactive, className],
          renderProps
        );
      }}
      data-variant={variant}
      data-padding={padding}
      data-radius={radius}
      {...props}
    />
  );
};

type CardHeaderFooterBaseProps = HTMLAttributes<HTMLElement> & {
  children: ReactNode;
  divider?: 'visible' | 'hidden';
  padding?: CardRootBaseProps['padding'];
};

export type BetaCardHeaderProps = CardHeaderFooterBaseProps;

/**
 * The header content of a `BetaCardRoot*` element (optional)
 */
export const BetaCardHeader = ({
  className,
  padding, // default value omitted, to inherit from parent via CSS
  divider = 'visible',
  ...props
}: BetaCardHeaderProps): ReactNode => {
  return (
    <div
      className={clsx(styles.header, className)}
      data-padding={padding}
      data-divider={divider}
      {...props}
    />
  );
};

export type BetaCardFooterProps = CardHeaderFooterBaseProps;

/**
 * The footer content of a `BetaCardRoot*` element (optional)
 */
export const BetaCardFooter = ({
  className,
  padding, // default value omitted, to inherit from parent via CSS
  divider = 'visible',
  ...props
}: BetaCardFooterProps): ReactNode => {
  return (
    <div
      className={clsx(styles.footer, className)}
      data-padding={padding}
      data-divider={divider}
      {...props}
    />
  );
};

export type BetaCardBodyProps = HTMLAttributes<HTMLElement> & {
  padding?: CardRootBaseProps['padding'];
  children: ReactNode;
};

/**
 * The body content of a `BetaCardRoot*` element (required)
 */
export const BetaCardBody = ({
  className,
  padding,
  ...props
}: BetaCardBodyProps): ReactNode => {
  return (
    <div
      className={clsx(styles.body, className)}
      data-padding={padding}
      {...props}
    />
  );
};

export type BetaCardGroupToggleButtonProps = ToggleButtonGroupProps;

export const BetaCardGroupToggleButton = (
  props: BetaCardGroupToggleButtonProps
) => {
  return <ToggleButtonGroup {...props} />;
};
